import { ActionIcon, Box, Button, Group, Modal, Stack, Text, ThemeIcon } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dayjs, { type Dayjs } from 'dayjs'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu'
import type { Assignment, AssignmentPriority } from '@/frontend/hooks/useAssignments'
import styles from './AssignmentCalendar.module.css'
import { StatusBadge } from './StatusBadge'

type Props = {
  assignments: Assignment[]
  onSelect: (id: string) => void
}

const PRIORITY_CLASS: Record<AssignmentPriority, string> = {
  URGENT: styles.chipUrgent,
  HIGH: styles.chipHigh,
  MEDIUM: styles.chipMedium,
  LOW: styles.chipLow,
}

const MAX_VISIBLE_PER_DAY = 3

export function AssignmentCalendar({ assignments, onSelect }: Props) {
  const { t, i18n } = useTranslation()
  const [cursor, setCursor] = useState<Dayjs>(() => dayjs().startOf('month'))
  const [openedDay, setOpenedDay] = useState<Dayjs | null>(null)
  const [modalOpened, modalH] = useDisclosure(false)

  const today = dayjs().startOf('day')

  const weekdayLabels = useMemo(() => {
    const start = dayjs().startOf('week').add(1, 'day')
    return Array.from({ length: 7 }, (_, i) => start.add(i, 'day').locale(i18n.language).format('ddd'))
  }, [i18n.language])

  const cells = useMemo(() => {
    const firstOfMonth = cursor.startOf('month')
    const offset = (firstOfMonth.day() + 6) % 7
    const gridStart = firstOfMonth.subtract(offset, 'day')
    const days: { date: Dayjs; inMonth: boolean }[] = []
    for (let i = 0; i < 42; i++) {
      const d = gridStart.add(i, 'day')
      days.push({ date: d, inMonth: d.month() === cursor.month() })
    }
    return days
  }, [cursor])

  const byDay = useMemo(() => {
    const m = new Map<string, Assignment[]>()
    for (const a of assignments) {
      if (!a.deadline) continue
      const k = dayjs(a.deadline).format('YYYY-MM-DD')
      const arr = m.get(k)
      if (arr) arr.push(a)
      else m.set(k, [a])
    }
    for (const arr of m.values()) {
      arr.sort((x, y) => {
        const order: Record<AssignmentPriority, number> = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }
        const px = order[x.priority ?? 'LOW']
        const py = order[y.priority ?? 'LOW']
        if (px !== py) return px - py
        return x.title.localeCompare(y.title)
      })
    }
    return m
  }, [assignments])

  const openDay = (d: Dayjs) => {
    setOpenedDay(d)
    modalH.open()
  }

  const dayAssignments = openedDay ? (byDay.get(openedDay.format('YYYY-MM-DD')) ?? []) : []

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Group className={styles.toolbar} gap={6}>
          <ActionIcon
            variant="default"
            radius="md"
            size="lg"
            onClick={() => setCursor((c) => c.subtract(1, 'month'))}
            aria-label={t('page.facility.tasks.cal_prev')}
          >
            <LuChevronLeft size={16} />
          </ActionIcon>
          <Text className={styles.title}>{cursor.locale(i18n.language).format('MMMM YYYY')}</Text>
          <ActionIcon
            variant="default"
            radius="md"
            size="lg"
            onClick={() => setCursor((c) => c.add(1, 'month'))}
            aria-label={t('page.facility.tasks.cal_next')}
          >
            <LuChevronRight size={16} />
          </ActionIcon>
          <Button size="xs" variant="light" color="laut" onClick={() => setCursor(dayjs().startOf('month'))}>
            {t('page.facility.tasks.cal_today')}
          </Button>
        </Group>
        <Group className={styles.legend} gap={12}>
          <span className={styles.legendItem}>
            <span className={styles.legendDot} style={{ background: 'var(--mantine-color-red-6)' }} />
            {t('page.facility.tasks.priority_urgent')}
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendDot} style={{ background: 'var(--mantine-color-orange-6)' }} />
            {t('page.facility.tasks.priority_high')}
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendDot} style={{ background: 'var(--mantine-color-laut-6)' }} />
            {t('page.facility.tasks.priority_medium')}
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendDot} style={{ background: 'var(--mantine-color-gray-5)' }} />
            {t('page.facility.tasks.priority_low')}
          </span>
        </Group>
      </Group>

      <div>
        <div className={styles.weekdayRow}>
          {weekdayLabels.map((w) => (
            <div key={w} className={styles.weekday}>
              {w}
            </div>
          ))}
        </div>
        <div className={styles.grid}>
          {cells.map(({ date, inMonth }) => {
            const key = date.format('YYYY-MM-DD')
            const items = byDay.get(key) ?? []
            const isToday = date.isSame(today, 'day')
            const visible = items.slice(0, MAX_VISIBLE_PER_DAY)
            const overflow = items.length - visible.length

            const cellClass = [styles.cell, !inMonth && styles.cellMuted, isToday && styles.cellToday]
              .filter(Boolean)
              .join(' ')

            const dayNumClass = [styles.dayNumber, !inMonth && styles.dayNumberMuted, isToday && styles.dayNumberToday]
              .filter(Boolean)
              .join(' ')

            return (
              <div key={key} className={cellClass}>
                <span className={dayNumClass}>{date.date()}</span>
                {visible.map((a) => {
                  const done = a.status === 'COMPLETED' || a.status === 'CANCELLED'
                  const priority = a.priority ?? 'LOW'
                  const chipClass = [styles.chip, PRIORITY_CLASS[priority], done && styles.chipDone]
                    .filter(Boolean)
                    .join(' ')
                  return (
                    <button
                      key={a.id}
                      type="button"
                      className={chipClass}
                      onClick={() => onSelect(a.id)}
                      title={a.title}
                    >
                      <span className={styles.chipText}>{a.title}</span>
                    </button>
                  )
                })}
                {overflow > 0 && (
                  <button type="button" className={styles.more} onClick={() => openDay(date)}>
                    +{overflow} {t('page.facility.tasks.cal_more')}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <Modal
        opened={modalOpened}
        onClose={modalH.close}
        title={openedDay ? openedDay.locale(i18n.language).format('dddd, DD MMMM YYYY') : ''}
        size="md"
        radius="md"
      >
        <Stack gap="xs">
          {dayAssignments.length === 0 ? (
            <Text c="dimmed" size="sm">
              {t('page.facility.tasks.cal_no_tasks')}
            </Text>
          ) : (
            dayAssignments.map((a) => {
              const done = a.status === 'COMPLETED' || a.status === 'CANCELLED'
              return (
                <Box
                  key={a.id}
                  p="sm"
                  style={{
                    borderRadius: 8,
                    cursor: 'pointer',
                    border: '1px solid var(--mantine-color-default-border)',
                    opacity: done ? 0.6 : 1,
                  }}
                  onClick={() => {
                    modalH.close()
                    onSelect(a.id)
                  }}
                >
                  <Group justify="space-between" wrap="nowrap" gap="sm">
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Text size="sm" fw={600} style={{ textDecoration: done ? 'line-through' : undefined }}>
                        {a.title}
                      </Text>
                      {a.assignedStaff?.name && (
                        <Text size="xs" c="dimmed">
                          {a.assignedStaff.name}
                        </Text>
                      )}
                    </div>
                    <Group gap={6} wrap="nowrap">
                      {a.priority && <StatusBadge status={a.priority} kind="assignmentPriority" upper />}
                      {a.status && <StatusBadge status={a.status} kind="assignmentStatus" upper />}
                    </Group>
                  </Group>
                </Box>
              )
            })
          )}
        </Stack>
      </Modal>

      {assignments.length === 0 && (
        <Group justify="center" gap="xs" py="md">
          <ThemeIcon variant="light" color="gray" size="sm" radius="xl">
            <LuChevronRight size={12} />
          </ThemeIcon>
          <Text size="sm" c="dimmed">
            {t('page.facility.tasks.cal_no_deadlines')}
          </Text>
        </Group>
      )}
    </Stack>
  )
}
